import { useEffect, useState, useContext } from "react";
import axios from "axios";

import { AppContext } from '../../context.js';
import { ChevronDown, MoreVertical, Refresh, CalendarEdit, ArrowRight, Close } from "../../files/icons/index.js";

import { Status, Button } from './component.js';
import Editor from "./editor.js";
import Drop from "./drop.js";
import { DatePicker } from "./datePicker.js";
import RecurrencePicker from "./recurrencePicker.js";
import PhysicianPicker from "./physicianPicker.js";
import { FullName } from '../../utils/string.js';
import { DateToFormat, DatetimeToDate, InRangeUTC } from "../../utils/datetime.js";
import { isCognitiveStagingTemplate } from '../../utils/assessments.js';

function draftFromTemplate(template, patientID) {
  const defaultStatus = (template.statuses || []).find((s) => s.id === template.defaultStatusID);
  return {
    id: null,
    templateID: template.id,
    templateName: template.title,
    patientID: patientID,
    title: template.title,
    description: template.description,
    statusType: defaultStatus ? defaultStatus.type : 'TO_DO',
    statusID: template.defaultStatusID,
    statusName: defaultStatus ? defaultStatus.title : '',
    questions: (template.questions || []).map((q) => ({
      id: q.id,
      title: q.title,
      description: q.description,
      answerType: q.answerType,
      answerOptions: q.answerOptions || [],
      answerValues: [],
      allowDecline: !!q.allowDecline,
      note: null,
    })),
    scores: [],
    userID: null,
    userName: null,
    deadlineAt: null,
    createdAt: null,
    closedAt: null,
  };
}

// AssessmentFormModal renders the full assessment form (mirroring physician/assessment.js)
// inside a modal. It has two modes:
// - VIEW (read-only) is the default when the owning task action's status is already true.
// - EDIT is the default for a brand-new (never-saved) assessment action. All fields other
//   than status/created-closed-at edit a local draft only; Save persists the draft via the
//   same per-field endpoints physician/assessment.js already uses, then reports success to
//   the parent (TaskCard) so it can check the task action. Cancel discards the draft.
//
// Create-from-template (no assessmentID, template + patientID provided): the form is
// seeded from the template and nothing is persisted until Save. Cancel/close leave
// no assessment record.
function AssessmentFormModal(props) {
  const context = useContext(AppContext);

  const { assessmentID, actionStatus, users, settings, onClose, onSaved, cognitiveStaging = false, onCognitiveStagingChange = null, template = null, patientID = null } = props;
  const createMode = !assessmentID && !!template;

  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(null);
  const [draft, setDraft] = useState(null);
  const [mode, setMode] = useState(actionStatus ? 'VIEW' : 'EDIT');
  const [saving, setSaving] = useState(false);

  const [closeAssessmentStatusDrop, setCloseAssessmentStatusDrop] = useState(0);
  const [changeAssessmentStatusModalVisible, setChangeAssessmentStatusModalVisible] = useState(false);
  const [changeAssessmentStatusModalData, setChangeAssessmentStatusModalData] = useState(null);

  const [updateAssessmentCreatedAtData, setUpdateAssessmentCreatedAtData] = useState({ visible: false, date: null, reason: null });
  const [updateAssessmentClosedAtData, setUpdateAssessmentClosedAtData] = useState({ visible: false, date: null, reason: null });

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  useEffect(() => {
    if (createMode) {
      const seeded = draftFromTemplate(template, patientID);
      setSaved(seeded);
      setDraft(seeded);
      setMode('EDIT');
      setLoading(false);
      return;
    }
    let u = context.addSpiner();
    axios.get(`/api/admin/assessments/${assessmentID}`).then((response) => {
      setSaved(response.data);
      setDraft(response.data);
      setMode(actionStatus ? 'VIEW' : 'EDIT');
      setLoading(false);
    }).catch((e) => {
      context.addError(e);
    }).finally(() => {
      context.removeSpiner(u);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentID, createMode]);

  function isDirty() {
    return JSON.stringify(draft) !== JSON.stringify(saved);
  }

  function hasAnswers() {
    return (draft?.questions || []).some((q) => (q.answerValues || []).length > 0);
  }

  function requestClose() {
    if (mode === 'EDIT' && (createMode ? hasAnswers() : isDirty())) {
      if (!window.confirm('Discard unsaved changes?')) {
        return;
      }
    }
    if (onClose) onClose();
  }

  function handleCancel() {
    if (onClose) onClose();
  }

  // Status changes commit immediately (same as physician/assessment.js), even while
  // the rest of the form is a local draft, since the reason-selection flow is already
  // its own confirmation step.
  function changeAssessmentStatus(status) {
    if (status.reasons.length > 0) {
      setChangeAssessmentStatusModalData({status});
      setChangeAssessmentStatusModalVisible(true);
      return;
    }
    updateAssessmentStatus(status.id, null);
  }

  function changeAssessmentStatusConfirm(statusID, reasonID) {
    setChangeAssessmentStatusModalVisible(false);
    setChangeAssessmentStatusModalData(null);
    updateAssessmentStatus(statusID, reasonID);
  }

  function updateAssessmentStatus(statusID, reasonID) {
    let u = context.addSpiner();
    axios.post('/api/admin/assessments/updateStatus', {
      id: draft.id,
      statusID: statusID,
      reasonID: reasonID,
    }).then((response) => {
      const merged = (prev) => ({
        ...prev,
        statusType: response.data.statusType,
        statusID: response.data.statusID,
        statusName: response.data.statusName,
        statusReasonID: response.data.statusReasonID,
        statusReasonName: response.data.statusReasonName,
        scores: response.data.scores,
        closedAt: response.data.closedAt,
        closedBy: response.data.closedBy,
        closedByName: response.data.closedByName,
      });
      setDraft(merged);
      setSaved(merged);
      setCloseAssessmentStatusDrop(prev => prev + 1);
      // A status change (e.g. canceling) can flip whether the patient still has an
      // active FAST/CDR, so let the parent refresh its one-per-patient check.
      if (cognitiveStaging && onCognitiveStagingChange) onCognitiveStagingChange();
    }).catch((e) => {
      context.addError(e);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function updateAssessmentCreatedAt() {
    let u = context.addSpiner();
    axios.post('/api/admin/assessments/updateCreatedAt', {
      id: draft.id,
      createdAt: updateAssessmentCreatedAtData.date,
      reason: updateAssessmentCreatedAtData.reason,
    }).then(() => {
      const merged = (prev) => ({ ...prev, createdAt: updateAssessmentCreatedAtData.date });
      setDraft(merged);
      setSaved(merged);
      setUpdateAssessmentCreatedAtData({ visible: false, date: null, reason: null });
    }).catch((e) => {
      context.addError(e);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function updateAssessmentClosedAt() {
    let u = context.addSpiner();
    axios.post('/api/admin/assessments/updateClosedAt', {
      id: draft.id,
      closedAt: updateAssessmentClosedAtData.date,
      reason: updateAssessmentClosedAtData.reason,
    }).then(() => {
      const merged = (prev) => ({ ...prev, closedAt: updateAssessmentClosedAtData.date });
      setDraft(merged);
      setSaved(merged);
      setUpdateAssessmentClosedAtData({ visible: false, date: null, reason: null });
    }).catch((e) => {
      context.addError(e);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function renderAssessmentStatus(templateID, statusID) {
    const template = settings.assessmentTemplates.find((t) => t.id === templateID);
    const status = template && template.statuses.find((s) => s.id === statusID);

    if (!status) {
      return <></>;
    }

    return (
      <Status color={status.color} muted={true} size={'small'} subtext={status.title} />
    );
  }

  function renderAssessmentNextStatuses(templateID, statusID) {
    const template = settings.assessmentTemplates.find((t) => t.id === templateID);
    const currentStatus = template && template.statuses.find((s) => s.id === statusID);

    if (!currentStatus) {
      return <></>;
    }

    if (!currentStatus.next) {
      return (
        <div className="typo-medium-150">
          Assessment has no next statuses
        </div>
      );
    }

    return template.statuses.filter((status) => currentStatus.next.includes(status.id)).map((status) => {
      return (
        <div key={status.id} className="side-item small typo-medium-150" onClick={() => changeAssessmentStatus(status)}>
          To <ArrowRight height={14} width={16} color={'grey'} /> {renderAssessmentStatus(templateID, status.id)}
        </div>
      );
    });
  }

  // Draft-only field editors (no network call until Save)
  function changeDraftTitle(text) {
    setDraft(prev => ({ ...prev, title: text }));
  }

  function keyUpDraftTitle(e) {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  }

  function changeDraftDescription(text) {
    if (text === "" || text === "<p><br></p>") {
      text = null;
    }
    setDraft(prev => ({ ...prev, description: text }));
  }

  function changeDraftUser(user) {
    setDraft(prev => ({ ...prev, userID: user.id, userName: FullName(user.firstName, user.middleName, user.lastName) }));
  }

  function changeDraftDeadline(date) {
    setDraft(prev => ({ ...prev, deadlineAt: date }));
  }

  function changeDraftRecurrence(id, intervalType, intervalDuration, fromType) {
    setDraft(prev => ({ ...prev, recurreType: intervalType, recurreDuration: intervalDuration, recurreFromType: fromType }));
  }

  function changeDraftAnswerValue(questionID, answerValues) {
    setDraft(prev => ({
      ...prev,
      questions: prev.questions.map((q) => q.id === questionID ? { ...q, answerValues } : q),
    }));
  }

  function changeDraftQuestionNote(note, questionID) {
    if (note === "" || note === "<p><br></p>") {
      note = null;
    }
    setDraft(prev => ({
      ...prev,
      questions: prev.questions.map((q) => q.id === questionID ? { ...q, note } : q),
    }));
  }

  function unansweredQuestionCount(questions) {
    return (questions || []).filter((q) => !q.answerValues || q.answerValues.length === 0).length;
  }

  async function persistAnswers(assessmentId, questions) {
    for (const q of questions || []) {
      await axios.post('/api/admin/assessments/updateAnswer', { id: assessmentId, questionID: q.id, answerValues: q.answerValues || [] });
      await axios.post('/api/admin/assessments/updateQuestionNote', { id: assessmentId, questionID: q.id, note: q.note });
    }
  }

  async function completeAssessment(assessmentId, completedStatus) {
    const response = await axios.post('/api/admin/assessments/updateStatus', {
      id: assessmentId,
      statusID: completedStatus.id,
      reasonID: null,
    });
    return {
      statusType: response.data.statusType,
      statusID: response.data.statusID,
      statusName: response.data.statusName,
      statusReasonID: response.data.statusReasonID,
      statusReasonName: response.data.statusReasonName,
      scores: response.data.scores,
      closedAt: response.data.closedAt,
      closedBy: response.data.closedBy,
      closedByName: response.data.closedByName,
    };
  }

  async function cancelCreatedAssessment(assessmentId, tmpl) {
    const canceledStatus = tmpl && tmpl.statuses && tmpl.statuses.find((s) => s.type === 'CANCELED');
    if (!canceledStatus) return;
    const reasonID = (canceledStatus.reasons && canceledStatus.reasons.length > 0) ? canceledStatus.reasons[0].id : null;
    try {
      await axios.post('/api/admin/assessments/updateStatus', {
        id: assessmentId,
        statusID: canceledStatus.id,
        reasonID,
      });
    } catch (_) {
      // Best-effort rollback so a failed complete does not leave a stray TO_DO FAST/CDR.
    }
  }

  function saveContext() {
    const tmpl = (template && createMode)
      ? template
      : (settings && settings.assessmentTemplates && settings.assessmentTemplates.find((t) => t.id === draft.templateID));
    const completedStatus = tmpl && tmpl.statuses && tmpl.statuses.find((s) => s.type === 'COMPLETED');
    const willComplete = completedStatus && (createMode || draft.statusType !== 'COMPLETED' || cognitiveStaging);
    return { tmpl, completedStatus, willComplete };
  }

  async function handleSave() {
    const { completedStatus } = saveContext();

    const unanswered = unansweredQuestionCount(draft.questions);
    if (createMode && unanswered > 0) {
      window.alert(`Cannot save: ${unanswered} question${unanswered === 1 ? ' is' : 's are'} unanswered`);
      return;
    }
    if (createMode && !completedStatus) {
      window.alert('Cannot save: template has no completed status');
      return;
    }

    await persistSave();
  }

  function finishSave(finalAssessment) {
    setSaved(finalAssessment);
    setDraft(finalAssessment);
    setMode('VIEW');
    setSaving(false);
    if (onSaved) onSaved(finalAssessment);
  }

  async function persistCreate({ tmpl, completedStatus }) {
    let createdId = null;
    let completedOk = false;
    try {
      const created = await axios.post('/api/admin/assessments/create', {
        patientID: patientID || draft.patientID,
        templateID: tmpl.id,
      });
      createdId = created.data.id;
      await persistAnswers(createdId, draft.questions);
      const completed = await completeAssessment(createdId, completedStatus);
      completedOk = true;
      finishSave({
        ...created.data,
        ...draft,
        id: createdId,
        createdAt: created.data.createdAt,
        questions: draft.questions,
        ...completed,
      });
    } catch (e) {
      if (createdId && !completedOk) await cancelCreatedAssessment(createdId, tmpl);
      throw e;
    }
  }

  async function persistUpdate({ completedStatus, willComplete }, existingId) {
    const assessmentId = existingId || draft.id;
    // Run updates sequentially — each endpoint opens its own Mongo transaction on
    // the same assessment document, so parallel writes hit WriteConflict.
    await axios.post('/api/admin/assessments/updateTitle', { id: assessmentId, title: draft.title });
    await axios.post('/api/admin/assessments/updateDescription', { id: assessmentId, description: draft.description });
    await axios.post('/api/admin/assessments/updateDeadline', { id: assessmentId, deadline: draft.deadlineAt });
    await axios.post('/api/admin/assessments/updateRecurrence', {
      id: assessmentId,
      intervalType: draft.recurreType,
      intervalDuration: draft.recurreDuration,
      fromType: draft.recurreFromType,
    });

    await persistAnswers(assessmentId, draft.questions);

    if (draft.userID) {
      await axios.post('/api/admin/assessments/updateUser', { id: assessmentId, userID: draft.userID });
    }

    // Transition the assessment to its template's COMPLETED status (after all
    // answers have been persisted, so score validation sees the saved values) so
    // it appears as a completed assessment on the patient's Assessments tab. Skip
    // if it is already completed or the template has no completed-type status —
    // except for cognitive staging (FAST/CDR), where editing a completed
    // assessment must re-run this transition so the stage is recalculated from
    // the edited answers.
    let finalAssessment = { ...draft, id: assessmentId };
    if (willComplete) {
      const completed = await completeAssessment(assessmentId, completedStatus);
      finalAssessment = { ...finalAssessment, ...completed };
    }
    finishSave(finalAssessment);
  }

  async function persistSave() {
    const save = saveContext();
    setSaving(true);
    try {
      if (createMode) {
        await persistCreate(save);
      } else {
        await persistUpdate(save);
      }
    } catch (e) {
      context.addError(e);
      setSaving(false);
    }
  }

  if (loading || !draft) {
    return <></>;
  }

  const readOnly = mode === 'VIEW';
  const isStagingTemplate = isCognitiveStagingTemplate(draft.templateID);

  var datePickerColor = "grey";
  if (draft.deadlineAt && draft.statusType !== "COMPLETED" && draft.statusType !== "CANCELED") {
    const today = new Date();
    const deadlineDate = new Date(draft.deadlineAt);

    const firstMomentOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0);
    const lastMomentOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999);

    const tomorrow = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
    const firstMomentOfTomorrow = new Date(tomorrow.getFullYear(), tomorrow.getMonth(), tomorrow.getDate(), 0, 0, 0, 0);
    const lastMomentOfTomorrow = new Date(tomorrow.getFullYear(), tomorrow.getMonth(), tomorrow.getDate(), 23, 59, 59, 999);

    if (InRangeUTC(deadlineDate, firstMomentOfTomorrow, lastMomentOfTomorrow)) {
      datePickerColor = "yellow";
    } else if (InRangeUTC(deadlineDate, firstMomentOfToday, lastMomentOfToday) || new Date().getTime() > deadlineDate.getTime()) {
      datePickerColor = "red";
    }
  }

  return (
    <div className="fixed-bottom modal-bg" onClick={requestClose}>
      <div className="container" style={{paddingTop: '5%'}}>
        <div className="row">
          <div className="col-xs-10 col-xs-offset-1 col-lg-8 col-lg-offset-2">
            <div className="card shadowmore" onClick={(e) => {e.stopPropagation()}} style={{maxHeight: '85vh', overflowY: 'auto'}}>

              <div className="card-header sm">
                <div className="d-flex justify-content-between align-items-center">
                  <input
                    type="text"
                    className="input borderless typo-strong-200 w-100"
                    placeholder="Title..."
                    disabled={readOnly || createMode}
                    value={draft.title || ""}
                    onChange={(e) => changeDraftTitle(e.target.value)}
                    onKeyUp={(e) => keyUpDraftTitle(e)}
                  />
                  <div className="d-flex justify-content-end align-items-center">
                    {!createMode && settings && renderAssessmentStatus(draft.templateID, draft.statusID)}
                    {!readOnly && !createMode && settings && (
                      <Drop close={closeAssessmentStatusDrop} side={"right"} className="ms-1">
                        <div className="item rect">
                          <MoreVertical height={14} width={16} color={'grey'} />
                        </div>
                        <div className="card" style={{width: 200}}>
                          <div className="card-header sm">
                            {!isStagingTemplate && (
                              <>
                                <div className="typo-normal-150 text-muted">Change status:</div>
                                {renderAssessmentNextStatuses(draft.templateID, draft.statusID)}
                              </>
                            )}
                            <div className="typo-normal-150 text-muted">Change dates:</div>
                            <div className="side-item small typo-medium-150" onClick={() => setUpdateAssessmentCreatedAtData({ visible: true, date: draft.createdAt, reason: null })}>
                              Change created at date
                            </div>
                            {draft.closedAt && (
                              <div className="side-item small typo-medium-150" onClick={() => setUpdateAssessmentClosedAtData({ visible: true, date: draft.closedAt, reason: null })}>
                                Change closed at date
                              </div>
                            )}
                          </div>
                        </div>
                      </Drop>
                    )}
                    <div className="item rect ms-1" onClick={requestClose}>
                      <Close height={14} width={16} color={'grey'} />
                    </div>
                  </div>
                </div>

                <Editor
                  placeholder="Assessment description..."
                  value={draft.description}
                  readOnly={readOnly || createMode}
                  onChangeID={draft.id || 'create'}
                  onBlur={(text) => changeDraftDescription(text)}
                  toolbar="limited"
                  className="text-editor borderless"
                />

                {(draft.scores && draft.scores.length > 0) && (
                  <div className="mt-1">
                    {draft.scores.map((score) => {
                      return (
                        <div key={"assessment_score_" + score.id} className="item grey me-2">
                          <div className="d-flex justify-content-start align-items-center">
                            <div>
                              <div className="typo-normal-150">{score.title}</div>
                              {score.description && (
                                <div className="typo-normal-150">{score.description}</div>
                              )}
                            </div>
                            <div className="typo-strong-150 ms-2">{score.score}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {!createMode && (
                  <div className="d-flex justify-content-start align-items-center mt-2">
                    <div className="d-flex justify-content-start align-items-center me-3">
                      <span className="typo-normal-150">created at {DateToFormat(DatetimeToDate(draft.createdAt), context.dateFormat)} {DateToFormat(DatetimeToDate(draft.createdAt), context.timeFormat === "us" ? "AMPM" : "HH:mm")}</span>
                    </div>
                    {draft.closedAt && (
                      <div className="d-flex justify-content-start align-items-center me-3">
                        <span className="typo-normal-150">closed at {DateToFormat(DatetimeToDate(draft.closedAt), context.dateFormat)} {DateToFormat(DatetimeToDate(draft.closedAt), context.timeFormat === "us" ? "AMPM" : "HH:mm")}</span>
                      </div>
                    )}
                  </div>
                )}

                {!createMode && (readOnly ? (
                  <div className="d-flex justify-content-start align-items-center mt-2">
                    <span className="typo-normal-150 me-3">Assignee: {draft.userName || 'Unassigned'}</span>
                    <span className="typo-normal-150 me-3">Deadline: {draft.deadlineAt ? DateToFormat(DatetimeToDate(draft.deadlineAt), context.dateFormat) : 'not set'}</span>
                  </div>
                ) : (
                  <div className="d-flex justify-content-start align-items-center mt-2">
                    <PhysicianPicker
                      physicianList={users}
                      physicianID={draft.userID}
                      onChange={changeDraftUser}
                      onChangeID={null}
                      rightIcon={<ChevronDown height={16} width={16} color={'grey'} />}
                      className="me-2"
                    />

                    <DatePicker
                      date={draft.deadlineAt}
                      onChange={changeDraftDeadline}
                      onChangeID={null}
                      leftIcon={<CalendarEdit height={16} width={16} color={'grey'} />}
                      rightIcon={<ChevronDown height={16} width={16} color={'grey'} />}
                      className={"me-2 " + datePickerColor}
                    />

                    <RecurrencePicker
                      recurreAt={draft.recurreAt}
                      onChange={changeDraftRecurrence}
                      onChangeID={draft.id}
                      leftIcon={<Refresh height={16} width={16} color={'grey'} />}
                      rightIcon={<ChevronDown height={16} width={16} color={'grey'} />}
                      className="me-2"
                    />
                  </div>
                ))}
              </div>

              {draft.questions.map((question, qi) => {
                return (
                  <div key={"question_" + qi} className="card-header sm">
                    <div className="typo-strong-175">{question.title}</div>
                    {question.description && (
                      <div className="typo-normal-150">{question.description}</div>
                    )}

                    {question.answerType === "text" && (() => {
                      const DECLINE_TEXT = "I choose not to answer this question";
                      const declined = question.answerValues && question.answerValues.length > 0 && question.answerValues[0] === DECLINE_TEXT;
                      return (
                        <div className="mt-1">
                          <input
                            type="text"
                            className="input borderless typo-normal-150 w-100"
                            placeholder="Answer..."
                            disabled={readOnly || declined}
                            value={declined ? "" : ((question.answerValues && question.answerValues.length > 0) ? question.answerValues[0] : "")}
                            onChange={(e) => changeDraftAnswerValue(question.id, e.target.value ? [e.target.value] : [])}
                          />
                          {question.allowDecline && (
                            <label className="d-flex align-items-center mt-1 typo-normal-150" style={{cursor: readOnly ? "default" : "pointer"}}>
                              <input
                                type="checkbox"
                                className="me-2"
                                disabled={readOnly}
                                checked={!!declined}
                                onChange={(e) => changeDraftAnswerValue(question.id, e.target.checked ? [DECLINE_TEXT] : [])}
                              />
                              I choose not to answer this question
                            </label>
                          )}
                        </div>
                      );
                    })()}

                    {(question.answerType === "options" || question.answerType === "optionsColumn") && (
                      <div className={question.answerType === "optionsColumn" ? "d-flex flex-column justify-content-start align-items-start mt-1" : "mt-1"}>
                        {question.answerOptions.map((option, oi) => {
                          const isSelected = question.answerValues && question.answerValues.includes(oi+"");
                          return (
                            <div
                              key={"answer_" + oi}
                              className={"item typo-normal-150 me-2 mb-1 " + (isSelected ? "blue" : "grey")}
                              style={{cursor: readOnly ? "default" : "pointer"}}
                              onClick={readOnly ? undefined : () => changeDraftAnswerValue(question.id, [oi+""])}
                            >
                              {option}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {(question.answerType === "checkboxes" || question.answerType === "checkboxesColumn") && (
                      <div className={question.answerType === "checkboxesColumn" ? "d-flex flex-column justify-content-start align-items-start mt-1" : "mt-1"}>
                        {question.answerOptions.map((option, oi) => {
                          const isSelected = question.answerValues && question.answerValues.includes(oi+"");
                          return (
                            <div
                              key={"answer_" + oi}
                              className={"item typo-normal-150 me-2 mb-1 " + (isSelected ? "blue" : "grey")}
                              style={{cursor: readOnly ? "default" : "pointer"}}
                              onClick={readOnly ? undefined : () => {
                                const currentValues = question.answerValues || [];
                                const optionIndex = oi+"";
                                const newValues = currentValues.includes(optionIndex)
                                  ? currentValues.filter(v => v !== optionIndex)
                                  : [...currentValues, optionIndex];
                                changeDraftAnswerValue(question.id, newValues);
                              }}
                            >
                              {option}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    <div className="mt-2">
                      <Editor
                        placeholder="Note..."
                        value={question.note}
                        readOnly={readOnly}
                        onChangeID={question.id}
                        onBlur={(note) => changeDraftQuestionNote(note, question.id)}
                        toolbar="limited"
                        className="text-editor borderless"
                      />
                    </div>
                  </div>
                );
              })}

              <div className="card-content sm text-end">
                {readOnly ? (
                  <Button onClick={() => setMode('EDIT')} color="blue" text="Edit" />
                ) : (
                  <>
                    <Button onClick={handleCancel} color="grey" text="Cancel" className="me-2" disabled={saving} />
                    <Button onClick={handleSave} color="blue" text={saving ? "Saving..." : "Save"} disabled={saving} />
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {updateAssessmentCreatedAtData.visible && (
        <div className="fixed-bottom modal-bg" onClick={(e) => { e.stopPropagation(); setUpdateAssessmentCreatedAtData({ visible: false }); }}>
          <div className="container-fluid" style={{paddingTop: '5%'}}>
            <div className="row">
              <div className="col-xs-10 col-xs-offset-1 col-lg-4 col-lg-offset-4">
                <div className="card shadowmore" onClick={(e) => {e.stopPropagation()}}>
                  <div className="card-header">
                    <span className="typo-header-3xLarge">Update Assessment Creation Date</span><br/>
                    <span className="typo-normal-175">Select the correct date and reason of creation and click update button</span>
                  </div>
                  <div className="card-content">
                    <div className="d-flex justify-content-start align-items-center">
                      <DatePicker
                        date={updateAssessmentCreatedAtData.date}
                        onChange={(date) => setUpdateAssessmentCreatedAtData({...updateAssessmentCreatedAtData, date: date})}
                        onChangeID={null}
                        leftIcon={<CalendarEdit height={16} width={16} color={'grey'} />}
                        rightIcon={<ChevronDown height={16} width={16} color={'grey'} />}
                        className="me-2"
                      />
                      <input
                        type="text"
                        className="input borderless typo-normal-150 w-100 ms-2"
                        placeholder="Reason..."
                        value={updateAssessmentCreatedAtData.reason || ""}
                        onChange={(e) => setUpdateAssessmentCreatedAtData({...updateAssessmentCreatedAtData, reason: e.target.value})}
                      />
                    </div>
                  </div>
                  <div className="card-content text-end">
                    <Button onClick={updateAssessmentCreatedAt} color="blue" text="Update" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {updateAssessmentClosedAtData.visible && (
        <div className="fixed-bottom modal-bg" onClick={(e) => { e.stopPropagation(); setUpdateAssessmentClosedAtData({ visible: false }); }}>
          <div className="container-fluid" style={{paddingTop: '5%'}}>
            <div className="row">
              <div className="col-xs-10 col-xs-offset-1 col-lg-4 col-lg-offset-4">
                <div className="card shadowmore" onClick={(e) => {e.stopPropagation()}}>
                  <div className="card-header">
                    <span className="typo-header-3xLarge">Update Assessment Completion Date</span><br/>
                    <span className="typo-normal-175">Select the correct date and reason of completion and click update button</span>
                  </div>
                  <div className="card-content">
                    <div className="d-flex justify-content-start align-items-center">
                      <DatePicker
                        date={updateAssessmentClosedAtData.date}
                        onChange={(date) => setUpdateAssessmentClosedAtData({...updateAssessmentClosedAtData, date: date})}
                        onChangeID={null}
                        leftIcon={<CalendarEdit height={16} width={16} color={'grey'} />}
                        rightIcon={<ChevronDown height={16} width={16} color={'grey'} />}
                        className="me-2"
                      />
                      <input
                        type="text"
                        className="input borderless typo-normal-150 w-100 ms-2"
                        placeholder="Reason..."
                        value={updateAssessmentClosedAtData.reason || ""}
                        onChange={(e) => setUpdateAssessmentClosedAtData({...updateAssessmentClosedAtData, reason: e.target.value})}
                      />
                    </div>
                  </div>
                  <div className="card-content text-end">
                    <Button onClick={updateAssessmentClosedAt} color="blue" text="Update" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {changeAssessmentStatusModalVisible && changeAssessmentStatusModalData && (
        <div className="fixed-bottom modal-bg" onClick={(e) => { e.stopPropagation(); setChangeAssessmentStatusModalVisible(false); }}>
          <div className="container-fluid" style={{paddingTop: '5%'}}>
            <div className="row">
              <div className="col-xs-10 col-xs-offset-1 col-lg-4 col-lg-offset-4">
                <div className="card shadowmore" onClick={(e) => {e.stopPropagation()}}>
                  <div className="card-header">
                    <span className="typo-header-3xLarge">Change Assessment Status</span><br/>
                    <span className="typo-normal-175">Select reason and click confirm button</span>
                  </div>
                  <div className="card-content">
                    {changeAssessmentStatusModalData.status.reasons.map((reason, ri) => {
                      return (
                        <div
                          key={"reason_" + ri}
                          className="side-item small typo-medium-150"
                          onClick={() => changeAssessmentStatusConfirm(changeAssessmentStatusModalData.status.id, reason.id)}
                        >
                          {reason.title}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AssessmentFormModal;
